export type AuditFieldChange = { field: string; from: string; to: string };

// A single diff row in payload order: either a name-only mention (opaque,
// truncated or redacted values, or a bare `fields` entry) or a full from→to change.
export type AuditDiffEntry =
  { kind: 'name'; field: string } | ({ kind: 'diff' } & AuditFieldChange);

// Unset value; the renderer shows it as a localized "none".
export const UNSET_VALUE = '—';

const formatChangeValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return UNSET_VALUE;
  }
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

// Long values with no whitespace (URLs, tokens, keys, configs) can't wrap, so they render name-only instead of overflowing the row.
const MAX_INLINE_VALUE_LENGTH = 80;

const isOpaqueValue = (value: string): boolean =>
  value.length > MAX_INLINE_VALUE_LENGTH && !/\s/.test(value);

// Shared by parseAuditLogDiff and hasAuditLogDiff so detection never diverges
// from what actually renders.
const getTrackedChanges = (extra: Record<string, unknown> | undefined): Record<string, unknown> => {
  const tracked = extra?.changes;
  if (tracked && typeof tracked === 'object' && !Array.isArray(tracked)) {
    return tracked as Record<string, unknown>;
  }
  return {};
};

const isRenderableEntry = (change: unknown): boolean => !!change && typeof change === 'object';

/**
 * Normalizes a log's `extra` (any API diff format: fields / field_changes /
 * changes) into a single list of diff entries in payload order. Each entry is
 * tagged `name` (bare field, opaque, truncated or redacted value) or `diff`
 * (from→to), so the caller renders one ordered loop.
 */
export const parseAuditLogDiff = (extra: Record<string, unknown> | undefined): AuditDiffEntry[] => {
  const entries: AuditDiffEntry[] = [];

  const pushChange = (field: string, from: string, to: string) => {
    if (isOpaqueValue(from) || isOpaqueValue(to)) {
      entries.push({ kind: 'name', field });
    } else {
      entries.push({ kind: 'diff', field, from, to });
    }
  };

  if (Array.isArray(extra?.fields)) {
    for (const field of extra.fields as string[]) {
      entries.push({ kind: 'name', field });
    }
  }

  if (Array.isArray(extra?.field_changes)) {
    for (const change of extra.field_changes as Array<{
      field: string;
      from: unknown;
      to: unknown;
    }>) {
      pushChange(change.field, formatChangeValue(change.from), formatChangeValue(change.to));
    }
  }

  for (const [field, change] of Object.entries(getTrackedChanges(extra))) {
    if (!isRenderableEntry(change)) {
      continue;
    }
    const entry = change as { before?: unknown; after?: unknown; truncated?: boolean };
    const isRedacted = entry.before === '[REDACTED]' || entry.after === '[REDACTED]';
    if (entry.truncated || isRedacted) {
      entries.push({ kind: 'name', field });
    } else {
      pushChange(field, formatChangeValue(entry.before), formatChangeValue(entry.after));
    }
  }

  return entries;
};

/** True when the log's `extra` carries a renderable field diff. */
export const hasAuditLogDiff = (extra: Record<string, unknown> | undefined): boolean =>
  (Array.isArray(extra?.fields) && extra.fields.length > 0) ||
  (Array.isArray(extra?.field_changes) && extra.field_changes.length > 0) ||
  Object.values(getTrackedChanges(extra)).some(isRenderableEntry);
